/**
 * SphereMesh surface, ported from Godot 4.6.3 `SphereMesh::create_mesh_array`
 * (`scene/resources/3d/primitive_meshes.cpp:1983-2088`): rows from the top pole down, U from +Z
 * towards +X, `height` squashing Y, and a hemisphere's lower rows folded onto a flat base.
 *
 * Derived from Godot Engine (`scene/resources/3d/primitive_meshes.cpp`), used under the MIT licence:
 *
 *   Copyright (c) 2014-present Godot Engine contributors (see AUTHORS.md).
 *   Copyright (c) 2007-2014 Juan Linietsky, Ariel Manzur.
 *
 *   Permission is hereby granted, free of charge, to any person obtaining
 *   a copy of this software and associated documentation files (the
 *   "Software"), to deal in the Software without restriction, including
 *   without limitation the rights to use, copy, modify, merge, publish,
 *   distribute, sublicense, and/or sell copies of the Software, and to
 *   permit persons to whom the Software is furnished to do so, subject to
 *   the following conditions:
 *
 *   The above copyright notice and this permission notice shall be
 *   included in all copies or substantial portions of the Software.
 *
 *   THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
 *   EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
 *   MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
 *   IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
 *   CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
 *   TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
 *   SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */
import { TAU } from '../../../godot/math/math.js';
import { storedReal } from '../../../godot/math/real.js';
import { normalizedVector3 } from '../../../godot/math/vector.js';
import { rimPoint } from '../primitivemesh/rimPoint.js';
import { writeRings } from '../primitivemesh/rows.js';
import { SurfaceWriter } from '../primitivemesh/surfaceWriter.js';
/** `uv2Padding` is in world units, or null for a mesh with no UV2 (`add_uv2` off). */
export function buildSphereMeshSurface(p, uv2Padding) {
    const { radius, height, radialSegments, rings, isHemisphere } = p;
    // Godot holds each value below in a `float`, and does `float` arithmetic on two of them, so each
    // such step rounds to single precision. A step with a `double` constant such as TAU does not.
    const scale = storedReal(storedReal(height / radius) * (isHemisphere ? 1 : 0.5));
    // UV2 lays the sphere out as one island, padded on the right and the bottom (`:1989-1993`).
    const padding = uv2Padding ?? 0;
    const circumference = storedReal(radius * TAU);
    const centerH = storedReal((0.5 * circumference) / storedReal(circumference + padding));
    const heightV = storedReal((scale * Math.PI) / (scale * Math.PI + storedReal(padding / radius)));
    const writer = new SurfaceWriter(uv2Padding !== null);
    const rowCount = rings + 1;
    writeRings(writer, rowCount, (j) => {
        const v = storedReal(j / rowCount);
        const lastRow = j === rowCount;
        const w = lastRow ? 0 : storedReal(Math.sin(Math.PI * v));
        const y = lastRow ? -1 : storedReal(Math.cos(Math.PI * v));
        for (let i = 0; i <= radialSegments; i++) {
            const u = storedReal(i / radialSegments);
            const [x, z] = rimPoint(i, radialSegments);
            const folded = isHemisphere && y < 0;
            const xw = storedReal(x * w);
            const zw = storedReal(z * w);
            const position = folded
                ? [storedReal(x * radius) * w, 0, storedReal(z * radius) * w]
                : [xw * radius, storedReal(y * scale) * radius, zw * radius];
            const normal = folded
                ? [0, -1, 0]
                : normalizedVector3(storedReal(xw * scale), y, storedReal(zw * scale));
            const wH = storedReal(w * 2 * centerH);
            writer.vertex(position, normal, [z, 0, -x, 1], [u, v], [centerH + (u - 0.5) * wH, v * heightV]);
        }
    });
    return writer.surface();
}
//# sourceMappingURL=build.js.map